'use client'

import { useEffect } from 'react'
import { clientLogger } from '@/lib/client-logger'

const log = clientLogger('window')

/**
 * Global catch-all for the client failures React error boundaries never see:
 * uncaught exceptions outside a render (window 'error') and unhandled promise
 * rejections — the most common class of production JS failure (a failed await
 * in an event handler, a fetch chain with no .catch). Reports go through
 * clientLogger, which handles throttling, dedupe, and the POST to
 * /api/client-errors.
 *
 * Renders nothing; mounted once in the root layout.
 */
export function GlobalErrorReporter() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      // event.error is the real Error when same-origin; cross-origin scripts
      // yield only "Script error." with no detail — still worth counting.
      log.error(
        'Uncaught error:',
        event.error instanceof Error ? event.error : event.message,
        { source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined }
      )
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      log.error(
        'Unhandled promise rejection:',
        event.reason instanceof Error ? event.reason : String(event.reason)
      )
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
    }
  }, [])

  return null
}
